"use client";

import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { statsApiUrl } from "../../utils/analytics";

const ADMIN_PASSWORD = "dashboard.kissariti.ma-2025-2026";

type StatsPayload = {
  ok: boolean;
  summary?: {
    live_now: number;
    visitors_today: number;
    visitors_yesterday: number;
    visitors_7d: number;
    visitors_30d: number;
    total_sessions: number;
    abandoned_carts: number;
    abandoned_value_mad: number;
    converted_carts: number;
  };
  daily?: Array<{ day: string; visitors: number; sessions: number }>;
  top_pages?: Array<{ path: string; views: number; unique_sessions: number }>;
  exit_pages?: Array<{ path: string; exits: number }>;
  page_exit_events?: Array<{ path: string; exits: number }>;
  sources?: Array<{ source: string; visitors: number }>;
  devices?: Array<{ device_type: string; sessions: number }>;
  abandoned_carts?: Array<{
    id: number;
    items_count: number;
    cart_value: string;
    last_page: string;
    abandoned_at: string;
    referrer_host: string | null;
    utm_source: string | null;
    cart_json: string;
  }>;
  recent_sessions?: Array<{
    session_uuid: string;
    first_seen_at: string;
    last_seen_at: string;
    landing_path: string;
    exit_path: string | null;
    referrer_host: string | null;
    utm_source: string | null;
    device_type: string;
    pageviews_count: number;
  }>;
  generated_at?: string;
  error?: string;
};

function StatCard({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string | number;
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded-xl border p-4 shadow-sm ${
        highlight ? "border-pink bg-pink/5" : "border-gray-200 bg-white"
      }`}
    >
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${highlight ? "text-pink" : "text-gray-900"}`}>
        {value}
      </p>
    </div>
  );
}

const AnalyticsDashboard: React.FC = () => {
  const [show, setShow] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [error, setError] = useState("");
  const [stats, setStats] = useState<StatsPayload | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Analytics | Kissariti";
    if (localStorage.getItem("open") === "true") {
      setShow(true);
    }
  }, []);

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(statsApiUrl(ADMIN_PASSWORD));
      const data = (await res.json()) as StatsPayload;
      if (!res.ok || !data.ok) {
        setError(data.error || "Impossible de charger les statistiques");
        setStats(null);
      } else {
        setStats(data);
      }
    } catch {
      setError("Erreur réseau");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (show) {
      loadStats();
      const interval = setInterval(loadStats, 60000);
      return () => clearInterval(interval);
    }
  }, [show, loadStats]);

  const subFunc = () => {
    if (keyword === ADMIN_PASSWORD) {
      localStorage.setItem("open", "true");
      setShow(true);
      setError("");
    } else {
      setError("Mot de passe incorrect");
    }
  };

  if (!show) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100 p-4">
        <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-lg">
          <h1 className="mb-4 text-xl font-bold text-gray-900">Analytics Kissariti</h1>
          <input
            type="password"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && subFunc()}
            className="mb-3 w-full rounded-lg border border-gray-300 px-3 py-2"
            placeholder="Mot de passe admin"
          />
          <button
            type="button"
            onClick={subFunc}
            className="w-full rounded-lg bg-pink py-2 font-semibold text-white hover:brightness-110"
          >
            Connexion
          </button>
          {error ? <p className="mt-3 text-center text-sm text-red-600">{error}</p> : null}
        </div>
      </div>
    );
  }

  const s = stats?.summary;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b border-gray-200 bg-white px-4 py-4 shadow-sm">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Analytics visiteurs</h1>
            <p className="text-sm text-gray-500">
              Dernière mise à jour : {stats?.generated_at || "—"}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={loadStats}
              disabled={loading}
              className="rounded-lg border border-pink px-4 py-2 text-sm font-semibold text-pink hover:bg-pink/10 disabled:opacity-50"
            >
              {loading ? "Chargement…" : "Actualiser"}
            </button>
            <Link
              to="/secret-page/dashboard-admin-200"
              className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              Admin
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-8 p-4 md:p-6">
        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">{error}</div>
        ) : null}

        {s ? (
          <section className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-5">
            <StatCard label="En ligne maintenant" value={s.live_now} highlight />
            <StatCard label="Visiteurs aujourd'hui" value={s.visitors_today} />
            <StatCard label="Hier" value={s.visitors_yesterday} />
            <StatCard label="7 derniers jours" value={s.visitors_7d} />
            <StatCard label="30 jours" value={s.visitors_30d} />
            <StatCard label="Sessions totales" value={s.total_sessions} />
            <StatCard label="Paniers abandonnés" value={s.abandoned_carts} />
            <StatCard label="Valeur abandonnée (MAD)" value={s.abandoned_value_mad} />
            <StatCard label="Commandes (panier converti)" value={s.converted_carts} />
          </section>
        ) : null}

        <section className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <h2 className="mb-3 font-bold text-gray-900">Pages les plus visitées (30j)</h2>
            <div className="max-h-80 overflow-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b text-gray-500">
                    <th className="py-2 pr-2">Page</th>
                    <th className="py-2">Vues</th>
                    <th className="py-2">Sessions</th>
                  </tr>
                </thead>
                <tbody>
                  {(stats?.top_pages || []).map((row) => (
                    <tr key={row.path} className="border-b border-gray-100">
                      <td className="max-w-[200px] truncate py-2 pr-2 font-mono text-xs">
                        {row.path}
                      </td>
                      <td className="py-2">{row.views}</td>
                      <td className="py-2">{row.unique_sessions}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <h2 className="mb-3 font-bold text-gray-900">Sources de trafic (30j)</h2>
            <ul className="space-y-2 text-sm">
              {(stats?.sources || []).map((row) => (
                <li
                  key={row.source}
                  className="flex justify-between rounded-lg bg-gray-50 px-3 py-2"
                >
                  <span className="font-medium">{row.source}</span>
                  <span className="text-gray-600">{row.visitors} visiteurs</span>
                </li>
              ))}
            </ul>
            <h3 className="mb-2 mt-6 font-semibold text-gray-800">Appareils</h3>
            <ul className="space-y-1 text-sm text-gray-600">
              {(stats?.devices || []).map((d) => (
                <li key={d.device_type}>
                  {d.device_type || "unknown"} : {d.sessions} sessions
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <h2 className="mb-3 font-bold text-gray-900">Où les visiteurs quittent le site</h2>
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b text-gray-500">
                  <th className="py-2">Page de sortie</th>
                  <th className="py-2">Nombre</th>
                </tr>
              </thead>
              <tbody>
                {(stats?.exit_pages || []).map((row) => (
                  <tr key={row.path} className="border-b border-gray-100">
                    <td className="max-w-[240px] truncate py-2 font-mono text-xs">{row.path}</td>
                    <td className="py-2">{row.exits}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <h2 className="mb-3 font-bold text-gray-900">Visiteurs par jour (14j)</h2>
            <ul className="max-h-80 space-y-1 overflow-auto text-sm">
              {(stats?.daily || []).map((row) => (
                <li
                  key={row.day}
                  className="flex justify-between rounded bg-gray-50 px-2 py-1.5"
                >
                  <span>{row.day}</span>
                  <span>
                    {row.visitors} visiteurs · {row.sessions} sessions
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 shadow-sm">
          <h2 className="mb-3 font-bold text-amber-950">Paniers abandonnés</h2>
          <p className="mb-4 text-sm text-amber-900/80">
            Visiteurs qui ont ajouté des articles au panier puis quitté le site sans commander.
          </p>
          <div className="space-y-3">
            {(stats?.abandoned_carts || []).length === 0 ? (
              <p className="text-sm text-gray-600">Aucun panier abandonné enregistré.</p>
            ) : (
              (stats?.abandoned_carts || []).map((row) => {
                let items: unknown[] = [];
                try {
                  items = JSON.parse(row.cart_json);
                } catch {
                  items = [];
                }
                return (
                  <div
                    key={row.id}
                    className="rounded-lg border border-amber-200/80 bg-white p-3 text-sm"
                  >
                    <div className="mb-2 flex flex-wrap justify-between gap-2">
                      <span className="font-semibold text-gray-900">
                        {row.items_count} article(s) · {Number(row.cart_value).toFixed(2)} MAD
                      </span>
                      <span className="text-xs text-gray-500">{row.abandoned_at}</span>
                    </div>
                    <p className="text-xs text-gray-600">
                      Dernière page : <code className="text-pink">{row.last_page}</code>
                    </p>
                    <p className="text-xs text-gray-500">
                      Source : {row.utm_source || row.referrer_host || "direct"}
                    </p>
                    <ul className="mt-2 list-inside list-disc text-xs text-gray-700">
                      {Array.isArray(items) &&
                        items.slice(0, 8).map((it: unknown, idx: number) => {
                          const item = it as Record<string, unknown>;
                          return (
                            <li key={idx}>
                              Produit #{String(item.id)} · qté {String(item.quantity ?? "?")}
                              {item.color ? ` · ${String(item.color)}` : ""}
                              {item.size ? ` · taille ${String(item.size)}` : ""}
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                );
              })
            )}
          </div>
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 font-bold text-gray-900">Sessions récentes</h2>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-xs">
              <thead>
                <tr className="border-b text-gray-500">
                  <th className="py-2 pr-2">Début</th>
                  <th className="py-2 pr-2">Dernière activité</th>
                  <th className="py-2 pr-2">Entrée</th>
                  <th className="py-2 pr-2">Sortie</th>
                  <th className="py-2 pr-2">Source</th>
                  <th className="py-2">Pages</th>
                </tr>
              </thead>
              <tbody>
                {(stats?.recent_sessions || []).map((row) => (
                  <tr key={row.session_uuid} className="border-b border-gray-100">
                    <td className="py-2 pr-2 whitespace-nowrap">{row.first_seen_at}</td>
                    <td className="py-2 pr-2 whitespace-nowrap">{row.last_seen_at}</td>
                    <td className="max-w-[120px] truncate py-2 pr-2">{row.landing_path}</td>
                    <td className="max-w-[120px] truncate py-2 pr-2">
                      {row.exit_path || "—"}
                    </td>
                    <td className="py-2 pr-2">
                      {row.utm_source || row.referrer_host || "direct"}
                    </td>
                    <td className="py-2">{row.pageviews_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
};

export default AnalyticsDashboard;
